import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  IconButton,
  ListItem,
  List,
  Menu,
  TreeDotsIcon,
  ListSeparator,
  EditIcon,
  TrashIcon,
} from "@activecollab/components";
import moment from "moment";
import PropTypes from "prop-types";
import { useSecurity } from "~/components/Hooks";
import { TrashOption } from "~/components/ModalObjects";
import { showErrorToast } from "~/components/Toast";
import { MOVE_CALENDAR_EVENT_TO_TRASH_SUCCESS } from "~/store/action_types";
import { addCalendarEventsExcludedDates, editCalendarEvent, moveToTrash } from "~/store/actions/actions.calendarEvents";
import { getCalendarById } from "~/store/selectors/selector.calendars";
import Angie from "~/utils/angie";
import { CalendarEventRescheduleDialog } from "./CalendarEventRescheduleDialog";
import { isRepeating } from "../Calendar/calendarUtils";
import { InsertLinkListItem } from "../InsertLinkListItem/InsertLinkListItem";
import { useObjectSheetContext } from "../Sheet/ObjectSheetContext";

export const CalendarEventOptions = ({ event, calendarId, onEdit = () => null }) => {
  const [threeDotsMenuOpen, setThreeDotsMenuOpen] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const { t } = useTranslation();
  const can = useSecurity();
  const canEditAndDelete = can(["edit"], event);
  const calendar = useSelector((state) => getCalendarById(state, calendarId));
  const [searchParams] = useSearchParams();
  const date = searchParams.get("date");
  const navigate = useNavigate();
  const { open, closeSheet } = useObjectSheetContext();
  const dispatch = useDispatch();

  const openThreeDotsMenu = useCallback(() => {
    setThreeDotsMenuOpen(true);
  }, []);
  const closeThreeDotsMenu = useCallback(() => setThreeDotsMenuOpen(false), []);

  const handleEdit = () => {
    onEdit();
    closeThreeDotsMenu();
  };

  const actionCallback = useCallback(
    (action) => {
      if (action?.error) {
        showErrorToast("Unable to move to trash");
      } else {
        if (action.type !== MOVE_CALENDAR_EVENT_TO_TRASH_SUCCESS) {
          if (open) {
            closeSheet();
          } else {
            navigate(`/calendar`);
          }
        }
        closeThreeDotsMenu();
      }
    },
    [closeSheet, closeThreeDotsMenu, navigate, open]
  );

  const handleMoveToTrash = useCallback(() => {
    dispatch(moveToTrash(event.id)).then(actionCallback);
  }, [actionCallback, dispatch, event.id]);

  const onMoveToTrash = () => {
    setOpenDialog(true);
  };

  const url = useMemo(() => {
    let url = `${Angie.root_url}/calendars/${calendarId}/events/${event.id}`;

    if (date) {
      url += `?date=${date}`;
      return url;
    }
    return url;
  }, [calendarId, date, event.id]);

  const handleSubmit = useCallback(
    (action) => {
      const date = moment.unix(event.starts_on).utc();
      switch (action) {
        case "only_this_event": {
          dispatch(
            addCalendarEventsExcludedDates(event.id, calendarId, {
              date: date.format(),
            })
          ).then(actionCallback);
          return;
        }
        case "this_and_following_events":
          dispatch(
            editCalendarEvent(event.id, calendarId, {
              repeat_until: date.add(-1, "day").startOf("day").format("YYYY-MM-DD"),
            })
          ).then(actionCallback);
          return;
        case "all_events":
          handleMoveToTrash();
          return;
        default:
          return "";
      }
    },
    [actionCallback, calendarId, dispatch, event.id, event.starts_on, handleMoveToTrash]
  );

  return (
    <Menu
      open={threeDotsMenuOpen}
      onOpen={openThreeDotsMenu}
      onClose={closeThreeDotsMenu}
      target={
        <IconButton
          variant="text gray"
          size="small"
          active={threeDotsMenuOpen}
        >
          <TreeDotsIcon />
        </IconButton>
      }
    >
      <List className="tw-py-2">
        {canEditAndDelete && calendar.class !== "ExternalCalendar" ? (
          <>
            <ListItem
              onClick={handleEdit}
              data-testid="edit-tree-dot"
            >
              <EditIcon /> {t("Edit")}
            </ListItem>
            <ListSeparator />
          </>
        ) : null}
        <InsertLinkListItem
          link={url}
          onCopy={closeThreeDotsMenu}
        />
        {canEditAndDelete && calendar.class !== "ExternalCalendar" ? (
          <>
            <ListSeparator />
            {!isRepeating(event) ? (
              <TrashOption
                type="event"
                onMoveToTrash={handleMoveToTrash}
              />
            ) : (
              <>
                <ListItem
                  onClick={onMoveToTrash}
                  data-testid="edit-tree-dot"
                >
                  <TrashIcon /> {t("Delete")}
                </ListItem>
                <CalendarEventRescheduleDialog
                  open={openDialog}
                  options={["only_this_event", "this_and_following_events", "all_events"]}
                  title={t("Delete Recurring Event")}
                  labelText={t("Which event do you want to delete?")}
                  explainerText={t("The events will remain in Trash for 30 days.")}
                  submitBtnText={t("Delete")}
                  onSubmit={handleSubmit}
                  onCancel={() => setOpenDialog(false)}
                />
              </>
            )}
          </>
        ) : null}
      </List>
    </Menu>
  );
};

CalendarEventOptions.propTypes = {
  event: PropTypes.object,
  calendarId: PropTypes.number,
  onEdit: PropTypes.func,
};
